import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useI18N } from "@sundae/react-hooks";
import { ExternalLink, Text, Tooltip, toolkitCx } from "@sundae/ui-toolkit";
import { ITextProps } from "@sundae/ui-toolkit/dist/types/components/atoms/Text/Text";
import { ITooltipProps } from "@sundae/ui-toolkit/dist/types/components/organisms/Tooltip/Tooltip";
import cx from "classnames";
import { FC, useMemo } from "react";
import { Trans } from "react-i18next";
import { externalRoutes } from "../../../../pages/routes";

interface INoDataTextProps extends Omit<ITextProps, "tag"> {
  iconSize?: ITextProps["size"];
  tooltipLabel?: ITooltipProps["tooltipLabel"];
}

const NoDataText: FC<INoDataTextProps> = ({
  className,
  iconSize = "base",
  size = "sm",
  tooltipLabel,
  ...textProps
}) => {
  const { t } = useI18N();

  const renderedContent = useMemo(() => {
    return (
      <Text
        {...textProps}
        className={cx("inline-flex items-center gap-1", className)}
        size={size}
        tag="div"
      >
        {t("N/A")}{" "}
        <ExclamationTriangleIcon
          className={cx(toolkitCx.layout.text.warning, toolkitCx.layout.size[iconSize])}
        />
      </Text>
    );
  }, []);

  return (
    <Tooltip
      trigger={renderedContent}
      tooltipLabel={
        tooltipLabel ?? (
          <Trans i18nKey="noData" ns="warnings" context="generic">
            Unable to display data. There may have been a temporary issue or an unexpected error.
            Please try again later or open a ticket in our{" "}
            <ExternalLink
              arrow="top-right"
              size="xs"
              href={externalRoutes.discord}
              target="_blank"
              rel="noopener noreferrer"
              variant="info"
            >
              Discord
            </ExternalLink>{" "}
            if the issue persists.
          </Trans>
        )
      }
    />
  );
};

export default NoDataText;
